(() => {
  const paths = {
    format: {
      title: 'Clarify the product format before the formula and package harden around it.',
      primary: ['Format Fit Finder', '/pages/manufacturing-guide'],
      secondary: ['Format & Delivery Guides', '/pages/manufacturing-guides'],
      summaries: {
        concept: 'Start by comparing the consumer experience, dose architecture, ingredient behavior, portability, and commercial priority.',
        development: 'Use the current formula assumptions to test whether the intended format still supports dose, stability, sensory, and use experience.',
        packaging: 'Confirm the format before final package dimensions, counts, closures, artwork, and case configuration are locked.',
        production: 'Recheck format assumptions against commercial process, run size, yield, equipment, and acceptance criteria.',
        release: 'Use the format result to explain the intended product experience and any unresolved tradeoffs during release review.'
      }
    },
    packaging: {
      title: 'Turn packaging from a visual decision into an executable system.',
      primary: ['Packaging Readiness Checklist', '/pages/packaging-checklist'],
      secondary: ['Packaging Readiness Guide', '/pages/packaging-readiness'],
      summaries: {
        concept: 'Identify the likely primary package and channel constraints early enough to influence format, serving count, and cost.',
        development: 'Connect product behavior, dose, fill, closure, protection, and user experience before artwork begins.',
        packaging: 'Work through container, closure, dieline, artwork, coding, case pack, logistics, and approval ownership now.',
        production: 'Confirm components, specifications, proofs, coding, line compatibility, and receiving timing before production setup.',
        release: 'Verify finished configuration, labels, lot coding, case pack, storage, routing, and fulfillment readiness before release.'
      }
    },
    documentation: {
      title: 'Make document ownership visible before missing information becomes a delay.',
      primary: ['Documentation Planner', '/pages/quality-overview'],
      secondary: ['Documentation & Traceability Guide', '/pages/documentation-traceability'],
      summaries: {
        concept: 'Define the early source of truth, intended claims, decision owners, and evidence needs before the concept fragments.',
        development: 'Map formula, specifications, sourcing, testing, label, approvals, and change control while the product is still evolving.',
        packaging: 'Connect approved copy, dielines, component specifications, proofs, coding, and release ownership.',
        production: 'Clarify batch records, specifications, test coordination, deviations, reconciliation, approvals, and release criteria.',
        release: 'Confirm which records, results, certificates, approvals, retention files, and finished-goods data are required for release.'
      }
    },
    scale: {
      title: 'Expose the assumptions that must become controlled inputs before commercial scale.',
      primary: ['Scale-Up Readiness Guide', '/pages/scale-up-readiness'],
      secondary: ['Release Readiness Scorecard', '/pages/release-readiness'],
      summaries: {
        concept: 'Use the guide to understand which formula, sourcing, process, packaging, cost, and volume assumptions will later require control.',
        development: 'Translate prototype learning into specifications, ingredient sources, process targets, yield expectations, and approval gates.',
        packaging: 'Confirm that commercial components, lead times, line compatibility, case configuration, and logistics support the scale plan.',
        production: 'Review formula lock, sourcing, process, yield, testing, packaging, forecast, and change control before the first commercial run.',
        release: 'Capture learning from the run and define what must change, remain controlled, or be monitored at the next volume tier.'
      }
    },
    release: {
      title: 'Score the unfinished decisions that could interrupt release or launch.',
      primary: ['Release Readiness Scorecard', '/pages/release-readiness'],
      secondary: ['Fulfillment Readiness Guide', '/pages/fulfillment-ready'],
      summaries: {
        concept: 'Use the scorecard early to see the complete commercialization system before work begins in separate functions.',
        development: 'Identify open formula, packaging, documentation, production, pricing, ownership, and timing decisions while there is room to act.',
        packaging: 'Check whether package, artwork, coding, quality, case pack, inventory, and channel requirements are converging on the same launch plan.',
        production: 'Score readiness before the run so unresolved supply, approval, documentation, and release dependencies have owners.',
        release: 'Use the scorecard as a final planning review—not as a release authorization—to prioritize gaps and assign next actions.'
      }
    }
  };

  function initNavigator(scope = document) {
    scope.querySelectorAll('[data-v51-resource-navigator]').forEach((section) => {
      if (section.dataset.bound) return;
      section.dataset.bound = 'true';
      const form = section.querySelector('[data-v51-resource-form]');
      const results = section.querySelector('[data-v51-resource-results]');
      const error = section.querySelector('[data-v51-resource-error]');
      const title = section.querySelector('[data-v51-resource-title]');
      const summary = section.querySelector('[data-v51-resource-summary]');
      const primary = section.querySelector('[data-v51-resource-primary]');
      const primaryTitle = section.querySelector('[data-v51-resource-primary-title]');
      const secondary = section.querySelector('[data-v51-resource-secondary]');
      const secondaryTitle = section.querySelector('[data-v51-resource-secondary-title]');
      const reset = section.querySelector('[data-v51-resource-reset]');
      if (!form || !results) return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const data = new FormData(form);
        const stage = data.get('stage');
        const decision = data.get('decision');
        if (!stage || !decision || !paths[decision]) {
          error.hidden = false;
          form.querySelector(':invalid')?.focus();
          return;
        }
        error.hidden = true;
        const path = paths[decision];
        title.textContent = path.title;
        summary.textContent = path.summaries[stage] || path.summaries.concept;
        primary.href = path.primary[1]; primaryTitle.textContent = path.primary[0];
        secondary.href = path.secondary[1]; secondaryTitle.textContent = path.secondary[0];
        results.hidden = false;
        results.classList.remove('is-entering');
        requestAnimationFrame(() => results.classList.add('is-entering'));
        results.focus?.({ preventScroll: true });
        window.dispatchEvent(new CustomEvent('biocorp:resource-recommendation', { detail: { stage, decision, primary: path.primary[0] } }));
      });
      reset?.addEventListener('click', () => {
        form.reset();
        results.hidden = true;
        error.hidden = true;
        form.querySelector('select')?.focus({ preventScroll: true });
      });
    });
  }

  function initInsightFilters(scope = document) {
    scope.querySelectorAll('[data-v51-insights]').forEach((library) => {
      if (library.dataset.filtersBound) return;
      library.dataset.filtersBound = 'true';
      const buttons = [...library.querySelectorAll('[data-v51-insight-filter]')];
      const cards = [...library.querySelectorAll('[data-v51-insight-card]')];
      const empty = library.querySelector('[data-v51-insight-empty]');
      buttons.forEach((button) => button.addEventListener('click', () => {
        const filter = button.dataset.v51InsightFilter;
        let visible = 0;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        cards.forEach((card) => {
          const show = filter === 'all' || card.dataset.category === filter;
          card.hidden = !show;
          if (show) { visible += 1; card.classList.add('is-filtered-in'); }
          else card.classList.remove('is-filtered-in');
        });
        if (empty) empty.hidden = visible !== 0;
        window.dispatchEvent(new CustomEvent('biocorp:insight-filter', { detail: { filter, visible } }));
      }));
    });
  }

  async function copyList(button) {
    const container = button.closest('.v51-insight-card__detail,.v51-guide__body');
    const source = container?.querySelector('[data-v51-copy-source]');
    if (!source) return;
    const lines = [...source.querySelectorAll('li')].map((item, index) => `${index + 1}. ${item.textContent.trim()}`);
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      const original = button.textContent;
      button.textContent = 'Copied';
      button.classList.add('is-copied');
      setTimeout(() => { button.textContent = original; button.classList.remove('is-copied'); }, 1800);
    } catch (_) {
      const area = document.createElement('textarea');
      area.value = text; area.setAttribute('readonly', ''); area.style.position = 'fixed'; area.style.opacity = '0';
      document.body.appendChild(area); area.select(); document.execCommand('copy'); area.remove();
      button.textContent = 'Copied';
    }
  }

  function initCopyButtons(scope = document) {
    scope.querySelectorAll('[data-v51-copy-list]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => copyList(button));
    });
  }

  function initGuideAccordion(scope = document) {
    scope.querySelectorAll('[data-v51-guide-library]').forEach((library) => {
      if (library.dataset.accordionBound) return;
      library.dataset.accordionBound = 'true';
      const guides = [...library.querySelectorAll('[data-v51-guide]')];
      guides.forEach((guide) => guide.addEventListener('toggle', () => {
        if (!guide.open) return;
        guides.forEach((other) => { if (other !== guide) other.removeAttribute('open'); });
        window.dispatchEvent(new CustomEvent('biocorp:guide-opened', { detail: { title: guide.querySelector('summary strong')?.textContent?.trim() } }));
      }));
    });
  }

  function init(scope = document) {
    initNavigator(scope);
    initInsightFilters(scope);
    initCopyButtons(scope);
    initGuideAccordion(scope);
  }
  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
